import React, { useState, useEffect } from 'react';
import { PrimeArtifact } from '../../types/primeArtifact';
import { ArtifactType } from '../../types/artifactTypes';
import { Omit } from 'utility-types';
import { getArtifactTypes, getPrimeArtifacts } from '../../services/primeArtifactService';

type PrimeArtifactFormData = Omit<PrimeArtifact, 'created_at' | 'updated_at' | 'children'>;

interface PrimeArtifactFormProps {
  onSubmit: (data: PrimeArtifactFormData) => void;
  initialData?: PrimeArtifactFormData;
}

export const PrimeArtifactForm: React.FC<PrimeArtifactFormProps> = ({ onSubmit, initialData }) => {
  const [formData, setFormData] = useState<PrimeArtifactFormData>(
    initialData || {
      id: '',
      name: '',
      description: '',
      artifact_type_id: '',
      prime_artifact_id: '',
      artifact_type_name: '',
      parent_ref: '',
      parent_name: '',
    }
  );
  const [artifactTypes, setArtifactTypes] = useState<ArtifactType[]>([]);
  const [parentArtifacts, setParentArtifacts] = useState<PrimeArtifact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [types, artifacts] = await Promise.all([getArtifactTypes(), getPrimeArtifacts()]);
        setArtifactTypes(types as ArtifactType[]);
        setParentArtifacts(artifacts.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name && formData.description && formData.artifact_type_id) {
      onSubmit(formData);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev: PrimeArtifactFormData) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="artifact-form">
      <h2>Create Prime Artifact</h2>

      <div className="form-group">
        <label htmlFor="name">
          Name <span className="required">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">
          Description <span className="required">*</span>
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description || ''}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="artifact_type_id">
          Artifact Type <span className="required">*</span>
        </label>
        <select
          id="artifact_type_id"
          name="artifact_type_id"
          value={formData.artifact_type_id}
          onChange={handleChange}
          required
        >
          <option value="">Select an artifact type</option>
          {artifactTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="parent_ref">Parent Reference</label>
        <select
          id="parent_ref"
          name="parent_ref"
          value={formData.parent_ref || ''}
          onChange={handleChange}
        >
          <option value="">No parent</option>
          {parentArtifacts.map((artifact) => (
            <option key={artifact.id} value={artifact.id}>
              {artifact.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-actions">
        <button type="submit" className="submit-button">
          Next: Define States
        </button>
      </div>
    </form>
  );
};
